import { useState } from "react";
import { motion } from "framer-motion";
import { FileText, ArrowRight, CheckCircle, Sparkles, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const benefits = [
  "Les 10 erreurs qui tuent votre crédibilité en ligne",
  "Le template de structure parfait pour votre secteur",
  "Checklist SEO pour être visible sur Google dès J+1",
  "3 exemples de portfolios qui convertissent à +15%",
];

const SellLeadMagnet = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Veuillez entrer un email valide");
      return;
    }
    setLoading(true);
    // Simulate sending — in production, connect to email service
    await new Promise((r) => setTimeout(r, 1200));
    setSent(true);
    setLoading(false);
    toast.success("Guide envoyé ! Vérifiez votre boîte mail 📬");
  };

  return (
    <section className="py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.04] to-transparent" />
      <div className="container mx-auto px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center p-8 md:p-10 rounded-3xl bg-gradient-to-br from-primary/10 via-card to-accent/10 border border-primary/20 shadow-xl shadow-primary/5 relative overflow-hidden">
            {/* Background glow */}
            <div className="absolute top-0 right-0 w-60 h-60 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-accent/10 rounded-full blur-3xl" />

            {/* Left — Content */}
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">
                <FileText className="w-3.5 h-3.5" />
                Guide gratuit — PDF
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-extrabold mb-3 leading-tight">
                10 erreurs fatales
                <br />
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  à éviter sur votre portfolio
                </span>
              </h3>
              <p className="text-muted-foreground text-sm mb-5">
                Le guide que 90% des indépendants auraient aimé lire avant de créer leur site.
              </p>
              <ul className="space-y-2.5">
                {benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground/90">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — Form */}
            <div className="relative">
              {!sent ? (
                <motion.div
                  className="p-6 rounded-2xl bg-card/80 backdrop-blur-sm border border-border/50 shadow-lg"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <span className="font-bold text-sm">Recevez le guide gratuitement</span>
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="votre@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 py-6 text-base"
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      size="lg"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-lg shadow-primary/25 group py-6"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                          Envoi en cours…
                        </span>
                      ) : (
                        <>
                          Recevoir mon guide gratuit
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                      🔒 Pas de spam. Désabonnement en 1 clic.
                    </p>
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-8 rounded-2xl bg-card/80 backdrop-blur-sm border border-primary/30 text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-primary" />
                  </div>
                  <h4 className="text-xl font-bold mb-2">Guide envoyé ! 🎉</h4>
                  <p className="text-muted-foreground text-sm mb-4">
                    Vérifiez votre boîte mail (et les spams, au cas où).
                  </p>
                  <a href="#pricing">
                    <Button variant="outline" className="border-primary/30 hover:bg-primary/10">
                      Voir les formules →
                    </Button>
                  </a>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SellLeadMagnet;
